<script lang="ts" setup>
import { ref } from "vue";
import { useForm, usePage, router } from "@inertiajs/vue3";
import ActionMessage from "../../../Jetstream/ActionMessage.vue";
import ActionSection from "../../../Jetstream/ActionSection.vue";
import ConfirmationModal from "../../../Jetstream/ConfirmationModal.vue";
import DangerButton from "../../../Jetstream/DangerButton.vue";
import DialogModal from "../../../Jetstream/DialogModal.vue";
import FormSection from "../../../Jetstream/FormSection.vue";
import InputError from "../../../Jetstream/InputError.vue";
import InputLabel from "../../../Jetstream/InputLabel.vue";
import PrimaryButton from "../../../Jetstream/PrimaryButton.vue";
import SecondaryButton from "../../../Jetstream/SecondaryButton.vue";
import SectionBorder from "../../../Jetstream/SectionBorder.vue";
import TextInput from "../../../Jetstream/TextInput.vue";
import route from "ziggy-js";

const props = defineProps({
    team: Object,
    availableRoles: Array,
    userPermissions: Object,
});

const addTeamMemberForm = useForm({
    email: "",
    role: null,
});

const updateRoleForm = useForm({
    role: null,
});

const leaveTeamForm = useForm({});
const removeTeamMemberForm = useForm({});

const currentlyManagingRole = ref(false);
const managingRoleFor = ref(null);
const confirmingLeavingTeam = ref(false);
const teamMemberBeingRemoved = ref(null);

const addTeamMember = () => {
    addTeamMemberForm.post(route("team-members.store", props.team), {
        errorBag: "addTeamMember",
        preserveScroll: true,
        onSuccess: () => addTeamMemberForm.reset(),
    });
};

const cancelTeamInvitation = (invitation) => {
    router.delete(route("team-invitations.destroy", invitation), {
        preserveScroll: true,
    });
};

const manageRole = (teamMember) => {
    managingRoleFor.value = teamMember;
    updateRoleForm.role = teamMember.membership.role;
    currentlyManagingRole.value = true;
};

const updateRole = () => {
    updateRoleForm.put(
        route("team-members.update", [props.team, managingRoleFor.value]),
        {
            preserveScroll: true,
            onSuccess: () => (currentlyManagingRole.value = false),
        },
    );
};

const confirmLeavingTeam = () => {
    confirmingLeavingTeam.value = true;
};

const leaveTeam = () => {
    leaveTeamForm.delete(
        route("team-members.destroy", [props.team, usePage().props.value.user]),
    );
};

const confirmTeamMemberRemoval = (teamMember) => {
    teamMemberBeingRemoved.value = teamMember;
};

const removeTeamMember = () => {
    removeTeamMemberForm.delete(
        route("team-members.destroy", [
            props.team,
            teamMemberBeingRemoved.value,
        ]),
        {
            errorBag: "removeTeamMember",
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => (teamMemberBeingRemoved.value = null),
        },
    );
};

const displayableRole = (role) => {
    return props.availableRoles.find((r) => r.key === role).name;
};
</script>

<template>
    <div>
        <div v-if="userPermissions.canAddTeamMembers">
            <SectionBorder />

            <!-- Add Team Member -->
            <FormSection @submitted="addTeamMember">
                <template #title>{{
                    $t("teams.team_settings.add_team_member.title")
                }}</template>

                <template #description>
                    {{ $t("teams.team_settings.add_team_member.description") }}
                </template>

                <template #form>
                    <div class="col-span-6">
                        <div class="max-w-xl text-sm text-iron-600">
                            {{
                                $t(
                                    "teams.team_settings.add_team_member.invitation_description",
                                )
                            }}
                        </div>
                    </div>

                    <!-- Member Email -->
                    <div class="sm:col-span-4 col-span-6">
                        <InputLabel
                            for="email"
                            :value="
                                $t('teams.team_settings.add_team_member.email')
                            "
                        />
                        <TextInput
                            id="email"
                            v-model="addTeamMemberForm.email"
                            type="email"
                            class="mt-1 block w-full"
                        />
                        <InputError
                            :message="addTeamMemberForm.errors.email"
                            class="mt-2"
                        />
                    </div>

                    <!-- Role -->
                    <div
                        v-if="availableRoles.length > 0"
                        class="lg:col-span-4 col-span-6"
                    >
                        <InputLabel
                            for="roles"
                            :value="
                                $t('teams.team_settings.add_team_member.role')
                            "
                        />
                        <InputError
                            :message="addTeamMemberForm.errors.role"
                            class="mt-2"
                        />

                        <div
                            class="mt-1 rounded-lg border-iron-200 relative z-0 cursor-pointer border"
                        >
                            <button
                                v-for="(role, i) in availableRoles"
                                :key="role.key"
                                type="button"
                                class="rounded-lg px-4 py-3 focus:border-blue-300 focus:ring-blue-200 relative inline-flex w-full focus:z-10 focus:ring focus:outline-none"
                                :class="{
                                    'border-iron-200 rounded-t-none border-t':
                                        i > 0,
                                    'rounded-b-none':
                                        i !=
                                        Object.keys(availableRoles).length - 1,
                                }"
                                @click="addTeamMemberForm.role = role.key"
                            >
                                <div
                                    :class="{
                                        'opacity-50':
                                            addTeamMemberForm.role &&
                                            addTeamMemberForm.role != role.key,
                                    }"
                                >
                                    <!-- Role Name -->
                                    <div class="flex items-center">
                                        <div
                                            class="text-sm text-iron-600"
                                            :class="{
                                                'font-semibold':
                                                    addTeamMemberForm.role ==
                                                    role.key,
                                            }"
                                        >
                                            {{
                                                $t(
                                                    "teams.roles." +
                                                        role.key +
                                                        ".title",
                                                )
                                            }}
                                        </div>

                                        <svg
                                            v-if="
                                                addTeamMemberForm.role ==
                                                role.key
                                            "
                                            class="ml-2 h-5 w-5 text-green-400"
                                            fill="none"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>

                                    <!-- Role Description -->
                                    <div
                                        class="mt-2 text-xs text-iron-600 text-left"
                                    >
                                        {{
                                            $t(
                                                "teams.roles." +
                                                    role.key +
                                                    ".description",
                                            )
                                        }}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </template>

                <template #actions>
                    <ActionMessage
                        :on="addTeamMemberForm.recentlySuccessful"
                        class="mr-3"
                    >
                        {{ $t("added") }}
                    </ActionMessage>

                    <PrimaryButton
                        :class="{ 'opacity-25': addTeamMemberForm.processing }"
                        :disabled="addTeamMemberForm.processing"
                    >
                        {{ $t("add") }}
                    </PrimaryButton>
                </template>
            </FormSection>
        </div>

        <div
            v-if="
                team.team_invitations.length > 0 &&
                userPermissions.canAddTeamMembers
            "
        >
            <SectionBorder />

            <!-- Team Member Invitations -->
            <ActionSection class="mt-10 sm:mt-0">
                <template #title>{{
                    $t("teams.team_settings.pending_team_invitations.title")
                }}</template>

                <template #description>
                    {{
                        $t(
                            "teams.team_settings.pending_team_invitations.description",
                        )
                    }}
                </template>

                <!-- Pending Team Member Invitation List -->
                <template #content>
                    <div class="space-y-6">
                        <div
                            v-for="invitation in team.team_invitations"
                            :key="invitation.id"
                            class="flex items-center justify-between"
                        >
                            <div class="text-iron-600">
                                {{ invitation.email }}
                            </div>

                            <div class="flex items-center">
                                <!-- Cancel Team Invitation -->
                                <button
                                    v-if="userPermissions.canRemoveTeamMembers"
                                    class="ml-6 text-sm text-error-500 cursor-pointer focus:outline-none"
                                    @click="cancelTeamInvitation(invitation)"
                                >
                                    {{ $t("cancel") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </ActionSection>
        </div>

        <div v-if="team.users.length > 0">
            <SectionBorder />

            <!-- Manage Team Members -->
            <ActionSection class="mt-10 sm:mt-0">
                <template #title>{{
                    $t("teams.team_settings.team_members.title")
                }}</template>

                <template #description>
                    {{ $t("teams.team_settings.team_members.description") }}
                </template>

                <!-- Team Member List -->
                <template #content>
                    <div class="space-y-6">
                        <div
                            v-for="user in team.users"
                            :key="user.id"
                            class="flex items-center justify-between"
                        >
                            <div class="flex items-center">
                                <img
                                    class="h-8 w-8 rounded-full"
                                    :src="user.profile_photo_url"
                                    :alt="user.name"
                                />
                                <div class="ml-4">
                                    {{ user.name }}
                                </div>
                            </div>

                            <div class="flex items-center">
                                <!-- Manage Team Member Role -->
                                <button
                                    v-if="
                                        userPermissions.canAddTeamMembers &&
                                        availableRoles.length
                                    "
                                    class="ml-2 text-sm text-iron-400 underline"
                                    @click="manageRole(user)"
                                >
                                    {{ displayableRole(user.membership.role) }}
                                </button>

                                <div
                                    v-else-if="availableRoles.length"
                                    class="ml-2 text-sm text-iron-400"
                                >
                                    {{ displayableRole(user.membership.role) }}
                                </div>

                                <!-- Leave Team -->
                                <button
                                    v-if="$page.props.user.id === user.id"
                                    class="ml-6 text-sm text-error-500 cursor-pointer"
                                    @click="confirmLeavingTeam"
                                >
                                    {{ $t("leave") }}
                                </button>

                                <!-- Remove Team Member -->
                                <button
                                    v-else-if="
                                        userPermissions.canRemoveTeamMembers
                                    "
                                    class="ml-6 text-sm text-error-500 cursor-pointer"
                                    @click="confirmTeamMemberRemoval(user)"
                                >
                                    {{ $t("remove") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </ActionSection>
        </div>

        <!-- Role Management Modal -->
        <DialogModal
            :show="currentlyManagingRole"
            @close="currentlyManagingRole = false"
        >
            <template #title>{{
                $t("teams.team_settings.manage_role.title")
            }}</template>

            <template #content>
                <div v-if="managingRoleFor">
                    <div
                        class="mt-1 rounded-lg border-iron-200 relative z-0 cursor-pointer border"
                    >
                        <button
                            v-for="(role, i) in availableRoles"
                            :key="role.key"
                            type="button"
                            class="rounded-lg px-4 py-3 focus:border-blue-300 focus:ring-blue-200 relative inline-flex w-full focus:z-10 focus:ring focus:outline-none"
                            :class="{
                                'border-iron-200 rounded-t-none border-t':
                                    i > 0,
                                'rounded-b-none':
                                    i !==
                                    Object.keys(availableRoles).length - 1,
                            }"
                            @click="updateRoleForm.role = role.key"
                        >
                            <div
                                :class="{
                                    'opacity-50':
                                        updateRoleForm.role &&
                                        updateRoleForm.role !== role.key,
                                }"
                            >
                                <!-- Role Name -->
                                <div class="flex items-center">
                                    <div
                                        class="text-sm text-iron-600"
                                        :class="{
                                            'font-semibold':
                                                updateRoleForm.role ===
                                                role.key,
                                        }"
                                    >
                                        {{
                                            $t(
                                                "teams.roles." +
                                                    role.key +
                                                    ".title",
                                            )
                                        }}
                                    </div>

                                    <svg
                                        v-if="updateRoleForm.role === role.key"
                                        class="ml-2 h-5 w-5 text-green-400"
                                        fill="none"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>

                                <!-- Role Description -->
                                <div class="mt-2 text-xs text-iron-600">
                                    {{
                                        $t(
                                            "teams.roles." +
                                                role.key +
                                                ".description",
                                        )
                                    }}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </template>

            <template #footer>
                <SecondaryButton @click="currentlyManagingRole = false">
                    {{ $t("cancel") }}
                </SecondaryButton>

                <PrimaryButton
                    class="ml-3"
                    :class="{ 'opacity-25': updateRoleForm.processing }"
                    :disabled="updateRoleForm.processing"
                    @click="updateRole"
                >
                    {{ $t("save") }}
                </PrimaryButton>
            </template>
        </DialogModal>

        <!-- Leave Team Confirmation Modal -->
        <ConfirmationModal
            :show="confirmingLeavingTeam"
            @close="confirmingLeavingTeam = false"
        >
            <template #title>{{
                $t("teams.team_settings.leave_team.title")
            }}</template>

            <template #content>
                {{ $t("teams.team_settings.leave_team.description") }}
            </template>

            <template #footer>
                <SecondaryButton @click="confirmingLeavingTeam = false">
                    {{ $t("cancel") }}
                </SecondaryButton>

                <DangerButton
                    class="ml-3"
                    :class="{ 'opacity-25': leaveTeamForm.processing }"
                    :disabled="leaveTeamForm.processing"
                    @click="leaveTeam"
                >
                    {{ $t("leave") }}
                </DangerButton>
            </template>
        </ConfirmationModal>

        <!-- Remove Team Member Confirmation Modal -->
        <ConfirmationModal
            :show="teamMemberBeingRemoved"
            @close="teamMemberBeingRemoved = null"
        >
            <template #title>{{
                $t("teams.team_settings.remove_team_member.title")
            }}</template>

            <template #content>
                {{ $t("teams.team_settings.remove_team_member.description") }}
            </template>

            <template #footer>
                <SecondaryButton @click="teamMemberBeingRemoved = null">
                    {{ $t("cancel") }}
                </SecondaryButton>

                <DangerButton
                    class="ml-3"
                    :class="{ 'opacity-25': removeTeamMemberForm.processing }"
                    :disabled="removeTeamMemberForm.processing"
                    @click="removeTeamMember"
                >
                    {{ $t("remove") }}
                </DangerButton>
            </template>
        </ConfirmationModal>
    </div>
</template>
